import { ApiQuery, ApiResponse, EncodeType, EncodeTypes } from "@/@types";
import { useMainStore } from "@/infra/store";
import router, { routes } from "@/presentation/router";
import axios, { AxiosResponse } from "axios";
import qs from "qs";
import { Http } from "./Http";

export const HttpService = (API_URL = process.env.VUE_APP_API_URL): Http => {
  return {
    get: async <R>(
      endpoint: string,
      query?: ApiQuery,
      encodeType?: EncodeType
    ): Promise<ApiResponse<R>> => {
      const queryString = query ? handleQuery(query) : "";
      const url = `${API_URL}${endpoint}?${qs.stringify(queryString)}`;
      return axios
        .get(url, { headers: handleHeaders(encodeType) })
        .then((response) => handleResponse<R>(response))
        .catch((error) => handleError(error));
    },
    delete: async <R>(endpoint: string): Promise<ApiResponse<R>> => {
      return axios
        .delete(API_URL + endpoint, { headers: handleHeaders() })
        .then((response) => handleResponse<R>(response))
        .catch((error) => handleError(error));
    },
    post: async <P, R>(
      endpoint: string,
      data: P,
      encodeType: EncodeType = EncodeTypes.JSON
    ): Promise<ApiResponse<R>> => {
      return axios
        .post(API_URL + endpoint, data, { headers: handleHeaders(encodeType) })
        .then((response) => handleResponse<R>(response))
        .catch((error) => handleError(error));
    },
    put: async <P, R>(
      endpoint: string,
      data: Partial<P>
    ): Promise<ApiResponse<R>> => {
      return axios
        .put(API_URL + endpoint, data, { headers: handleHeaders() })
        .then((response) => handleResponse<R>(response))
        .catch((error) => handleError(error));
    },
  };
};

export const handleQuery = (query: ApiQuery) => {
  return Object.fromEntries(
    Object.entries(query).filter(([_, valor]) => Boolean(valor))
  );
};

export const handleHeaders = (
  encodeType: EncodeType = EncodeTypes.JSON,
  defaultToken = ""
) => {
  const store = useMainStore();
  const access_token = store.token || defaultToken;
  const headers: { [key: string]: string } = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (encodeType === EncodeTypes.MULTIPART) {
    headers["Content-Type"] = "multipart/form-data";
  }
  if (encodeType === EncodeTypes.URLENCODED) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
  }
  if (access_token) {
    headers.Authorization = `Bearer ${access_token}`;
  }
  return headers;
};

export const handleResponse = <T>(response: AxiosResponse): ApiResponse<T> => {
  if (!response.data.status) {
    handleError({ message: response.data.message });
  }
  return {
    status: response.status,
    ...response.data,
  };
};

export const handleError = <T>(error: any = null): ApiResponse<T> => {
  if (error && error.status && error.status === 401) {
    router.push(routes.login.path);
    throw new Error("Sessão expirada.");
  }
  let message =
    error?.data?.message || error?.message || "Ocorreu um erro no servidor";
  if (error?.data?.errors) {
    const keys = Object.keys(error.data?.errors);
    message = error.data?.errors[keys[0]][0];
  }
  throw new Error(message);
};
